import { Component, Host, Prop, State, h } from '@stencil/core';
import { getConfig } from '@sydle/ui-utils';
import { type BlogConfig } from '../../../../types/blog';

export declare type LanguageLink = {
  title: string;
  link: string;
  hreflang: string;
};

@Component({
  tag: 'sy-blog-language-buttons',
  styleUrl: 'sy-blog-language-buttons.scss',
  shadow: true,
})
export class SyBlogLanguageButtons {
  @State() dropdownElement: HTMLSyDropdownElement;

  @State() selected = '';

  @State() defaultLanguage;

  @State() dropdownOpened = false;

  /**
   * Available languages with fallback links in case alternate page is not available
   */
  @Prop() languages: LanguageLink[];

  /**
   * Enable to remove language active, only type select
   */
  @Prop() removeActive = false;

  private getLinkPathname(link: string) {
    const absoluteUrlRegex = new RegExp('^(?:[a-z+]+:)?//', 'i'); // ref: https://stackoverflow.com/questions/10687099/how-to-test-if-a-url-string-is-absolute-or-relative
    if (link.match(absoluteUrlRegex)) {
      return new URL(link).pathname;
    }
    return new URL(link, window.location.href).pathname;
  }

  /**
   * Returns true if pathname of window has the same language path of the given pathname
   * @param pathname
   * @returns
   */
  private isPathOfCurrentLanguage(pathname: string) {
    const pathLanguageRegex = /^(\/\w{2}(-\w{2})?)(?:$|\/)/; // First directory consists of exactly 2 alphanumeric and is followed by / or end of string
    const currentPathLanguage = window.location.pathname.match(pathLanguageRegex)?.[1];
    const linkPathLanguage = pathname.match(pathLanguageRegex)?.[1];
    return currentPathLanguage === linkPathLanguage;
  }

  private observer: MutationObserver;

  private updateLanguageHrefs(alternateLinks: HTMLLinkElement[]) {
    const alternates = alternateLinks.filter((links) => links.rel === 'alternate');
    if (alternates.length > 0) {
      this.languages = this.languages.map((lang) => {
        const alternateLink = alternateLinks.find(
          (link) => link.hreflang.split('-')[0] === lang.hreflang.split('-')[0] && link.type !== 'application/rss+xml',
        );
        return alternateLink
          ? { ...lang, link: alternateLink.href }
          : this.defaultLanguage.find((defLang) => defLang.hreflang.split('-')[0] === lang.hreflang.split('-')[0]);
      });
    }
  }

  private handleCategoryDropdownOpen(): void {
    this.dropdownOpened = true;
  }

  private handleCategoryDropdownClose(): void {
    this.dropdownOpened = false;
  }

  componentWillLoad() {
    this.languages =
      getConfig<BlogConfig>('sydle-ui-blog')?.configByLanguage?.map((config) => ({
        title: config.language.name,
        link: `/${config.urlPath ? config.urlPath + '/' : ''}blog/`,
        hreflang: config.hreflang,
      })) || [];
    this.defaultLanguage = this.languages;
    this.updateLanguageHrefs(Array.from(document.head.querySelectorAll('link[rel=alternate]')));
    this.observer = new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach((mutation) => {
        const linkNodes = Array.from(mutation.addedNodes).filter((node) => node.nodeName === 'LINK');
        this.updateLanguageHrefs(linkNodes.map((node) => node as HTMLLinkElement));
      });
    });
    this.observer.observe(document.head, { childList: true });

    this.languages.forEach((language) => {
      if (this.isPathOfCurrentLanguage(this.getLinkPathname(language.link))) {
        this.selected = language.title;
        if (this.removeActive) return;
      }
    });
  }

  render() {
    return (
      <Host>
        <div id="languages-dropdown" data-sy-viewport>
          <a href="#" onClick={(e) => e.preventDefault()}>
            <sy-text id="selected-language-name">{this.selected}</sy-text>
            <sy-icon
              style={{ display: this.dropdownOpened ? 'none' : 'inline-block' }}
              name="keyboard_arrow_down"
              size="8"
            />
            <sy-icon
              style={{ display: this.dropdownOpened ? 'inline-block' : 'none' }}
              name="keyboard_arrow_up"
              size="8"
            />
          </a>
          <sy-dropdown
            onSyDropdownDidOpen={() => this.handleCategoryDropdownOpen()}
            onSyDropdownDidClose={() => this.handleCategoryDropdownClose()}
            toggle-by="#languages-dropdown"
            offset-distance="0"
            placement="bottom-end"
            key="dropdown"
            ref={(el) => (this.dropdownElement = el)}
          >
            <div class="languages">
              {this.languages?.map((language, index) => (
                <a
                  target="_self"
                  class={{
                    'link-select': true,
                    active: this.isPathOfCurrentLanguage(this.getLinkPathname(language.link)),
                  }}
                  href={language.link}
                  key={index}
                  onClick={() => this.dropdownElement.close()}
                >
                  <span class="inner-link">
                    <sy-text>{language.title}</sy-text>
                  </span>
                </a>
              ))}
            </div>
          </sy-dropdown>
        </div>
      </Host>
    );
  }
}
